import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { documentService } from '../../services/documentService';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loader from '../../components/Loader';
import TipTapEditor from '../../components/TipTapEditor';
import SearchableSelect from '../../components/common/SearchableSelect';

const PUBLIC_BASE = process.env.REACT_APP_WEBSITE_URL || 'https://www.geekdas.in';

const INITIAL_FORM = {
   title: '',
   content: '',
   status: 'ACTIVE',
   expiresAt: '',
   hasExpiry: false,
   pinEnabled: false,
   pinCode: '',
};

const DocumentForm = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEdit = Boolean(id);

   const [formData, setFormData] = useState(INITIAL_FORM);
   const [attachments, setAttachments] = useState([]);
   const [loading, setLoading] = useState(isEdit);
   const [saving, setSaving] = useState(false);
   const [regenerating, setRegenerating] = useState(false);
   const [uploadingFile, setUploadingFile] = useState(false);
   const [token, setToken] = useState('');
   const [hasPinCode, setHasPinCode] = useState(false);
   const fileInputRef = useRef(null);

   useEffect(() => {
      if (!isEdit) return;
      (async () => {
         try {
            setLoading(true);
            const res = await documentService.getById(id);
            const d = res.data;
            setToken(d.token);
            setHasPinCode(d.hasPinCode);
            setAttachments(d.attachments || []);
            setFormData({
               title: d.title || '',
               content: d.content || '',
               status: d.status || 'ACTIVE',
               expiresAt: d.expiresAt ? d.expiresAt.slice(0, 16) : '',
               hasExpiry: !!d.expiresAt,
               pinEnabled: d.pinEnabled || false,
               pinCode: '',
            });
         } catch {
            toast.error('Failed to load document');
            navigate('/admin/documents');
         } finally {
            setLoading(false);
         }
      })();
   }, [id, isEdit, navigate]);

   const set = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

   const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
         setUploadingFile(true);
         const res = await documentService.uploadAttachment(file);
         setAttachments((prev) => [...prev, res.data]);
         toast.success('File uploaded');
      } catch {
         toast.error('Upload failed');
      } finally {
         setUploadingFile(false);
         fileInputRef.current.value = '';
      }
   };

   const removeAttachment = (index) => {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!formData.title.trim()) return toast.error('Title is required');
      if (!formData.content.trim()) return toast.error('Document content is required');
      if (formData.hasExpiry && !formData.expiresAt)
         return toast.error('Please select an expiry date');
      if (formData.pinEnabled && !isEdit && !formData.pinCode)
         return toast.error('PIN is required when PIN protection is enabled');
      if (formData.pinEnabled && formData.pinCode && !/^\d{4}$/.test(formData.pinCode))
         return toast.error('PIN must be exactly 4 digits');

      try {
         setSaving(true);
         const { hasExpiry, ...rest } = formData;
         const payload = {
            ...rest,
            attachments,
            expiresAt: hasExpiry ? formData.expiresAt || null : null,
            pinCode: formData.pinCode || undefined,
         };

         if (isEdit) {
            await documentService.update(id, payload);
            toast.success('Document updated');
         } else {
            await documentService.create(payload);
            toast.success('Document created');
            navigate('/admin/documents');
         }
      } catch (err) {
         toast.error(err.response?.data?.error || 'Failed to save document');
      } finally {
         setSaving(false);
      }
   };

   const handleRegenerate = async () => {
      if (!window.confirm('Regenerate token? The old shareable link will stop working.')) return;
      try {
         setRegenerating(true);
         const res = await documentService.regenerateToken(id);
         setToken(res.data.token);
         toast.success('Token regenerated');
      } catch {
         toast.error('Failed to regenerate token');
      } finally {
         setRegenerating(false);
      }
   };

   const copyLink = () => {
      navigator.clipboard.writeText(`${PUBLIC_BASE}/document/${token}`);
      toast.success('Link copied');
   };

   const formatBytes = (bytes) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1048576).toFixed(1)} MB`;
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <form onSubmit={handleSubmit} className="space-y-8 w-full">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                     {isEdit ? 'Edit Document' : 'New Document'}
                  </h1>
                  {isEdit && token && (
                     <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                           {PUBLIC_BASE}/document/{token}
                        </span>
                        <button
                           type="button"
                           onClick={copyLink}
                           className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                           Copy
                        </button>
                        <button
                           type="button"
                           onClick={handleRegenerate}
                           disabled={regenerating}
                           className="text-xs text-orange-600 dark:text-orange-400 hover:underline disabled:opacity-50"
                        >
                           {regenerating ? 'Regenerating...' : 'Regenerate link'}
                        </button>
                     </div>
                  )}
               </div>
               <div className="flex gap-3">
                  <Button
                     type="button"
                     variant="secondary"
                     onClick={() => navigate('/admin/documents')}
                  >
                     Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                     {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Document'}
                  </Button>
               </div>
            </div>

            {/* Basic Info */}
            <section className="bg-white dark:bg-dark-secondary rounded-lg shadow-sm p-6 space-y-5">
               <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Basic Information
               </h2>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Document Title <span className="text-red-500">*</span>
                     </label>
                     <Input
                        value={formData.title}
                        onChange={(e) => set('title', e.target.value)}
                        placeholder="e.g. Company Handbook 2026"
                     />
                  </div>
                  <div>
                     <SearchableSelect
                        label="Status"
                        searchable={false}
                        options={[
                           { id: 'ACTIVE', name: 'Active' },
                           { id: 'INACTIVE', name: 'Inactive' },
                        ]}
                        value={formData.status}
                        onChange={(value) => set('status', value)}
                        className="!mb-0"
                     />
                  </div>
               </div>

               <div className="space-y-5">
                  {/* Expiry */}
                  <div className="flex items-center gap-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                     <div className="flex flex-col gap-4">
                        <div>
                           <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              Set Expiry Date
                           </p>
                           <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Off by default document link stays valid forever
                           </p>
                        </div>
                        <button
                           type="button"
                           onClick={() => set('hasExpiry', !formData.hasExpiry)}
                           className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent focus:ring-offset-2 ${
                              formData.hasExpiry ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                           }`}
                           role="switch"
                           aria-checked={formData.hasExpiry}
                        >
                           <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                 formData.hasExpiry ? 'translate-x-6' : 'translate-x-1'
                              }`}
                           />
                        </button>
                     </div>

                     {formData.hasExpiry && (
                        <div>
                           <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Expiry Date &amp; Time <span className="text-red-500">*</span>
                           </label>
                           <Input
                              type="datetime-local"
                              value={formData.expiresAt}
                              onChange={(e) => set('expiresAt', e.target.value)}
                              className="!mb-0"
                           />
                        </div>
                     )}
                  </div>

                  {/* PIN Protection */}
                  <div className="flex items-center gap-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                     <div className="flex flex-col gap-4">
                        <div>
                           <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              PIN Protection
                           </p>
                           <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Require a 4-digit PIN to view this document
                           </p>
                        </div>
                        <button
                           type="button"
                           onClick={() => set('pinEnabled', !formData.pinEnabled)}
                           className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent focus:ring-offset-2 ${
                              formData.pinEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                           }`}
                           role="switch"
                           aria-checked={formData.pinEnabled}
                        >
                           <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                 formData.pinEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                           />
                        </button>
                     </div>

                     {formData.pinEnabled && (
                        <div>
                           <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {isEdit
                                 ? hasPinCode
                                    ? 'New PIN (leave blank to keep existing)'
                                    : 'Set PIN'
                                 : 'PIN'}
                              {!isEdit && <span className="text-red-500 ml-1">*</span>}
                           </label>
                           <Input
                              type="text"
                              inputMode="numeric"
                              maxLength={4}
                              value={formData.pinCode}
                              onChange={(e) => {
                                 const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                                 set('pinCode', val);
                              }}
                              placeholder="••••"
                              className="!mb-0 w-28 font-mono tracking-[0.5em] text-center"
                           />
                        </div>
                     )}
                  </div>
               </div>
            </section>

            {/* Document Content */}
            <section className="bg-white dark:bg-dark-secondary rounded-lg shadow-sm p-6 space-y-4">
               <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Document Content <span className="text-red-500">*</span>
               </h2>
               <TipTapEditor
                  content={formData.content}
                  onChange={(content) => set('content', content)}
               />
            </section>

            {/* Attachments */}
            <section className="bg-white dark:bg-dark-secondary rounded-lg shadow-sm p-6 space-y-4">
               <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Attachments
               </h2>

               {attachments.length > 0 && (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                     {attachments.map((att, i) => (
                        <li
                           key={i}
                           className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800"
                        >
                           <div className="min-w-0 flex-1">
                              <a
                                 href={att.url}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block"
                              >
                                 {att.name}
                              </a>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                 {att.type} · {formatBytes(att.size)}
                              </span>
                           </div>
                           <button
                              type="button"
                              onClick={() => removeAttachment(i)}
                              className="ml-4 text-red-500 hover:text-red-700 dark:hover:text-red-400 text-sm"
                           >
                              Remove
                           </button>
                        </li>
                     ))}
                  </ul>
               )}

               <div className="flex items-center gap-3">
                  <input
                     ref={fileInputRef}
                     type="file"
                     accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                     onChange={handleFileUpload}
                     className="hidden"
                     id="attachment-upload"
                  />
                  <label
                     htmlFor="attachment-upload"
                     className={`cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-primary hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${uploadingFile ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                     {uploadingFile ? 'Uploading...' : 'Add File'}
                  </label>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                     Images, PDF, Word, Excel, TXT max 20 MB
                  </span>
               </div>
            </section>
         </form>
      </PanelLayout>
   );
};

export default DocumentForm;
